import {
  CategoryInfo,
  FinancialItems,
  financialAssetOptions,
} from 'src/financial/financial-data/hooks/useFinancialItems';
import { getFinancialItemType } from 'src/financial/financial-data/tables/FinancialItemGenericTable';

export type FinancialRow = {
  id: string;
  /** Client the item hangs off, so a row can be opened from a view that spans several. */
  clientId?: string;
  memberName: string;
  /** Every owner's name, for the family view, where no single member is "me". */
  ownerNames?: string[];
  // The item owner's share for the individual view: a number renders as "<n>%",
  // null renders as "—" (joint but no share set), undefined falls back to memberName.
  ownershipPercentage?: number | null;
  product: string;
  navProduct: string;
  description?: string;
  provider?: string;
  typeLabel?: string;
  balanceDate?: string;
  balance: number | null;
  isUpdatedAutomatically?: boolean;
  hasFeedData?: boolean;
};

type OwnershipContext = { clientId: string };

type FinancialItemWithOwners = {
  clientId: string;
  isJoint?: boolean | null;
  owners?: {
    clientId?: string | null;
    externalName?: string | null;
    ownershipPercentage?: number | null;
  }[];
};

/**
 * Resolves each recorded owner to a display name: a family member through the roster, an
 * external owner through its own name. Owners neither can name are dropped, so a part-external
 * item still reads differently from a solely owned one.
 */
function ownerNames(
  item: FinancialItemWithOwners,
  resolveOwnerName: ((clientId: string) => string | undefined) | undefined,
): string[] | undefined {
  if (!resolveOwnerName) return undefined;
  const names = (item.owners ?? [])
    .map(owner => (owner.clientId ? resolveOwnerName(owner.clientId) : owner.externalName))
    .filter((name): name is string => !!name);
  return names.length > 0 ? names : undefined;
}

function ownerShare(
  item: FinancialItemWithOwners,
  ownership: OwnershipContext | undefined,
): number | null | undefined {
  if (!ownership) return undefined;
  if (!item.isJoint) return 100;
  const mine = item.owners?.find(owner => owner.clientId === ownership.clientId);
  return mine?.ownershipPercentage ?? null;
}

export type FinancialSubSection = {
  key: string;
  title: string;
  rows: FinancialRow[];
  total: number;
};

type FinancialCategoryKey = 'spendingAccounts' | 'assets' | 'liabilities' | 'protections';

export type FinancialCategory = {
  key: FinancialCategoryKey;
  title: string;
  rows: FinancialRow[];
  total: number;
  totalLabel?: string;
  subSections?: FinancialSubSection[];
};

type GroupedRows = {
  spendingAccounts: FinancialRow[];
  assets: FinancialRow[];
  liabilities: FinancialRow[];
  protections: FinancialRow[];
};

export const emptyGroupedRows = (): GroupedRows => ({
  spendingAccounts: [],
  assets: [],
  liabilities: [],
  protections: [],
});

const sumBalances = (rows: FinancialRow[]) =>
  rows.reduce((acc, row) => acc + (row.balance ?? 0), 0);

export function extractRows(
  items: FinancialItems,
  memberName: string,
  ownership?: OwnershipContext,
  resolveOwnerName?: (clientId: string) => string | undefined,
): GroupedRows {
  const { metadata } = items;

  return {
    spendingAccounts: items.spendingAccounts.map(item => ({
      id: item.id,
      clientId: item.clientId,
      memberName,
      ownerNames: ownerNames(item, resolveOwnerName),
      ownershipPercentage: ownerShare(item, ownership),
      product: item.product,
      navProduct: item.product,
      description: item.description,
      provider: item.provider,
      typeLabel: getFinancialItemType(item, metadata),
      balanceDate: item.balanceDate,
      balance: item.currentBalance,
    })),
    assets: items.assets.map(item => ({
      id: item.id,
      clientId: item.clientId,
      memberName,
      ownerNames: ownerNames(item, resolveOwnerName),
      ownershipPercentage: ownerShare(item, ownership),
      product: item.product,
      navProduct: 'pensionProduct' in item ? item.pensionProduct : item.product,
      description: item.description,
      provider: item.provider,
      typeLabel: getFinancialItemType(item, metadata),
      balanceDate: item.valueDate,
      balance: item.value,
      isUpdatedAutomatically: item.isUpdatedAutomatically,
      hasFeedData: item.hasFeedData,
    })),
    liabilities: items.liabilities.map(item => ({
      id: item.id,
      clientId: item.clientId,
      memberName,
      ownerNames: ownerNames(item, resolveOwnerName),
      ownershipPercentage: ownerShare(item, ownership),
      product: item.product,
      navProduct: item.product,
      description: item.description,
      provider: item.provider,
      typeLabel: getFinancialItemType(item, metadata),
      balanceDate: item.remainingBalanceDate,
      balance: item.remainingBalance,
    })),
    protections: items.protections.map(item => ({
      id: item.id,
      clientId: item.clientId,
      memberName,
      ownerNames: ownerNames(item, resolveOwnerName),
      ownershipPercentage: ownerShare(item, ownership),
      product: item.product,
      navProduct: item.product,
      description: item.description,
      provider: item.provider,
      typeLabel: getFinancialItemType(item, metadata),
      balanceDate: undefined,
      balance: item.coverAmount ?? null,
    })),
  };
}

export function mergeGroupedRows(groups: GroupedRows[]): GroupedRows {
  return {
    spendingAccounts: groups.flatMap(group => group.spendingAccounts),
    assets: groups.flatMap(group => group.assets),
    liabilities: groups.flatMap(group => group.liabilities),
    protections: groups.flatMap(group => group.protections),
  };
}

function buildSubSections(
  category: 'ASSET' | 'LIABILITY',
  rows: FinancialRow[],
): FinancialSubSection[] {
  const categoryInfo: CategoryInfo = financialAssetOptions[category];
  const knownProducts = new Set<string>(categoryInfo.productsOrder);
  const sections = categoryInfo.productsOrder
    .map(product => {
      const productRows = rows.filter(row => row.product === product);
      return {
        key: product,
        title: categoryInfo.products[product].label,
        rows: productRows,
        total: sumBalances(productRows),
      };
    })
    .filter(section => section.rows.length > 0);

  // Capture any rows whose product isn't in productsOrder so the displayed
  // sub-sections always reconcile with the section total.
  const otherRows = rows.filter(row => !knownProducts.has(row.product));
  if (otherRows.length > 0) {
    sections.push({ key: 'OTHER', title: 'Other', rows: otherRows, total: sumBalances(otherRows) });
  }

  return sections;
}

export function buildCategories(grouped: GroupedRows): FinancialCategory[] {
  return [
    {
      key: 'spendingAccounts',
      title: 'Spending accounts',
      rows: grouped.spendingAccounts,
      total: sumBalances(grouped.spendingAccounts),
    },
    {
      key: 'assets',
      title: 'Assets',
      rows: grouped.assets,
      total: sumBalances(grouped.assets),
      subSections: buildSubSections('ASSET', grouped.assets),
    },
    {
      key: 'liabilities',
      title: 'Liabilities',
      rows: grouped.liabilities,
      total: sumBalances(grouped.liabilities),
      subSections: buildSubSections('LIABILITY', grouped.liabilities),
    },
    {
      key: 'protections',
      title: 'Protection policies',
      rows: grouped.protections,
      total: sumBalances(grouped.protections),
      totalLabel: 'Total cover',
    },
  ];
}
